/* istanbul ignore file */
import * as codec from 'purify-ts/Codec';
import { array as codecArray, boolean as codecBoolean, number as codecNumber, oneOf as codecOneOf, string as codecString, } from 'purify-ts/Codec';
import { parsers } from '@codegrade/utils';
import { AssignmentAnonymizationAlgo } from './AssignmentAnonymizationAlgo';
import { AssignmentKind } from './AssignmentKind';
import { AssignmentPeerFeedbackSettings } from './AssignmentPeerFeedbackSettings';
import { AssignmentPercentageGradingSettings } from './AssignmentPercentageGradingSettings';
import { AssignmentPointsGradingSettings } from './AssignmentPointsGradingSettings';
import { AssignmentSubmissionMode } from './AssignmentSubmissionMode';
import { CGIgnoreVersion } from './CGIgnoreVersion';
import { FixedGradeAvailability } from './FixedGradeAvailability';
import { GroupSet } from './GroupSet';
import { SubmissionValidatorInputData } from './SubmissionValidatorInputData';
import { TimeframeLike } from './TimeframeLike';
import { WebhookConfigurationDisabledAsJSON } from './WebhookConfigurationDisabledAsJSON';
import { WebhookConfigurationEnabledAsJSON } from './WebhookConfigurationEnabledAsJSON';
const AssignmentParser = /* #__PURE__ */ /* #__PURE__ */ codec.intersect(
/* #__PURE__ */ parsers.lazy(() => 
/* #__PURE__ */ parsers.object([
    ['id', codecNumber],
    ['hasDescription', codecBoolean, 'has_description'],
    ['createdAt', parsers.moment, 'created_at'],
    ['name', codecString],
    ['isLti', codecBoolean, 'is_lti'],
    ['courseId', codecNumber, 'course_id'],
    [
        'cgignore',
        /* #__PURE__ */ parsers.maybeNull(
        /* #__PURE__ */ codecOneOf([SubmissionValidatorInputData, codecString])),
    ],
    [
        'cgignoreVersion',
        /* #__PURE__ */ parsers.maybeNull(CGIgnoreVersion),
        'cgignore_version',
    ],
    ['sendLoginLinks', codecBoolean, 'send_login_links'],
    [
        'fixedMaxRubricPoints',
        /* #__PURE__ */ parsers.maybeNull(codecNumber),
        'fixed_max_rubric_points',
    ],
    ['maxGrade', codecNumber, 'max_grade'],
    [
        'grading',
        /* #__PURE__ */ parsers.taggedUnion('scale', [
            ['percentage', AssignmentPercentageGradingSettings],
            ['points', AssignmentPointsGradingSettings],
        ]),
    ],
    ['groupSet', /* #__PURE__ */ parsers.maybeNull(GroupSet), 'group_set'],
    ['autoTestId', /* #__PURE__ */ parsers.maybeNull(codecNumber), 'auto_test_id'],
    ['filesUploadEnabled', codecBoolean, 'files_upload_enabled'],
    [
        'webhookConfiguration',
        /* #__PURE__ */ parsers.taggedUnion('tag', [
            ['enabled', WebhookConfigurationEnabledAsJSON],
            ['disabled', WebhookConfigurationDisabledAsJSON],
        ]),
        'webhook_configuration',
    ],
    ['editorUploadEnabled', codecBoolean, 'editor_upload_enabled'],
    [
        'maxSubmissions',
        /* #__PURE__ */ parsers.maybeNull(codecNumber),
        'max_submissions',
    ],
    ['coolOffPeriod', parsers.duration, 'cool_off_period'],
    ['amountInCoolOffPeriod', codecNumber, 'amount_in_cool_off_period'],
    [
        'reminderTime',
        /* #__PURE__ */ parsers.maybeNull(codecString),
        'reminder_time',
    ],
    ['lmsName', /* #__PURE__ */ parsers.maybeNull(codecString), 'lms_name'],
    [
        'peerFeedbackSettings',
        /* #__PURE__ */ parsers.maybeNull(AssignmentPeerFeedbackSettings),
        'peer_feedback_settings',
    ],
    ['doneType', /* #__PURE__ */ parsers.maybeNull(codecString), 'done_type'],
    ['doneEmail', /* #__PURE__ */ parsers.maybeNull(codecString), 'done_email'],
    [
        'divisionParentId',
        /* #__PURE__ */ parsers.maybeNull(codecNumber),
        'division_parent_id',
    ],
    [
        'analyticsWorkspaceIds',
        /* #__PURE__ */ codecArray(codecNumber),
        'analytics_workspace_ids',
    ],
    ['kind', AssignmentKind],
    [
        'anonymizedGrading',
        /* #__PURE__ */ parsers.maybeNull(AssignmentAnonymizationAlgo),
        'anonymized_grading',
    ],
    [
        'fileToLoadFirst',
        /* #__PURE__ */ parsers.maybeNull(codecString),
        'file_to_load_first',
    ],
    ['gradeAvailability', FixedGradeAvailability, 'grade_availability'],
    ['hasMultipleTimeframes', codecBoolean, 'has_multiple_timeframes'],
    ['submissionMode', AssignmentSubmissionMode, 'submission_mode'],
])), TimeframeLike);
export class Assignment extends TimeframeLike {
    static decode(input) {
        return AssignmentParser.decode(input).map(res => new Assignment(res));
    }
    static unsafeDecode(input) {
        return Assignment.decode(input).unsafeCoerce();
    }
    static encode(data) {
        return AssignmentParser.encode(data);
    }
    static schema() {
        return AssignmentParser.schema();
    }
}
